import { z } from 'zod';
import {
  phoneNumberSchema,
  requiredPhoneNumberSchema,
} from '@trashlab/api/src/domains/core/validation/phone-number-schema';
import { requiredStreetAddress } from '@trashlab/api/src/domains/core/validation/address-schema';
import { dateOnlySchema } from '@trashlab/api/src/domains/core/validation/date-schema';
import { ShopSteps } from '@trashlab/api/src/domains/consumer/types';

export const siteAddressSchema = z.object({
  streetAddressPartOne: z.string().min(1, 'Street address is required'),
  streetAddressPartTwo: z.string(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  county: z.string().optional().nullable(),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
});

const submitAddressAndWasteGenerator = z.object({
  cartId: z.uuid(),
  tenantId: z.uuid(),
  siteAddress: siteAddressSchema,
  customerSegmentId: z.uuid().optional().nullable(),
  wasteGeneratorCategory: z.enum(['RESIDENTIAL', 'COMMERCIAL']),
});

const submitPricing = z.object({
  cartId: z.uuid(),
  tenantId: z.uuid(),
  materialId: z.uuid().nullable(),
  productId: z.uuid().nullable(),
  priceCardId: z.uuid().nullable(),
  // V2 recurring selections for subscription shop
  subscriptionSettingsId: z.uuid().nullable().optional(),
  recurrenceSettingsId: z.uuid().nullable().optional(),
});

const submitNotesAndOthers = z.object({
  cartId: z.uuid(),
  tenantId: z.uuid(),
  customerNotes: z.string().optional(),
  siteAddress: requiredStreetAddress,
  containerRotation: z.number().default(0),
  attachments: z
    .array(
      z.object({
        fileId: z.uuid(),
        url: z.string(),
        type: z.string(),
        notes: z.string(),
      }),
    )
    .optional(),
});

const submitServiceDate = z.object({
  cartId: z.uuid(),
  tenantId: z.uuid(),
  deliveryServiceDate: dateOnlySchema,
  pickupServiceDate: z
    .preprocess(
      (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
      dateOnlySchema.nullable(),
    )
    .optional(),
  daysOver: z.number().default(0),
  isWebsite: z.boolean().optional().default(false),
  noContainerRentalNeeded: z.boolean().optional().default(false),
});

const submitServiceAgreement = z
  .object({
    cartId: z.uuid(),
    tenantId: z.uuid(),
    contractTemplateId: z.string().optional().nullable(),
    signedFirstName: z.string().optional().nullable(),
    signedLastName: z.string().optional().nullable(),
    signedIp: z.string().optional().nullable(),
    signedStyle: z.string().optional().nullable(),
    signedAtTs: z.string().optional().nullable(),
  })
  .refine(
    (data) => {
      if (data.contractTemplateId) {
        return (
          data.signedFirstName && data.signedLastName && data.signedStyle
          // data.signedAtTs
        );
      }
      return true;
    },
    {
      message: 'All fields must be provided when contractTemplateId is present',
      path: ['contractTemplateId'],
    },
  );

// Base object shape shared between the mutation (lenient) and form validation (strict).
// The mutation schema is lenient so the debounced auto-save can persist partial contact
// info (e.g. only phone entered so far) without failing validation. The form schema
// keeps strict rules so the user can't complete checkout with missing required fields.
const submitContactBase = z.object({
  customerBusinessName: z.string().nullable(),
  cartId: z.uuid(),
  tenantId: z.uuid(),
  siteDetailsSameAsBilling: z.boolean(),
  customerAddress: z.object({
    streetAddressPartOne: z.string(),
    streetAddressPartTwo: z.string(),
    city: z.string(),
    state: z.string(),
    postalCode: z.string(),
  }),
  customerContactId: z.uuid().optional().nullable(),
  customerEmail: z.string(),
  customerName: z.string(),
  customerPhoneNumber: phoneNumberSchema,
  siteContactEmail: z.string(),
  siteContactName: z.string(),
  siteContactPhoneNumber: phoneNumberSchema,
});

// Lenient schema used by the tRPC mutation — accepts partial contact info
// so the debounced auto-save can persist whatever the user has entered so far.
const submitContact = submitContactBase;

// Strict schema used by the Mantine form resolver — requires customerName and
// customerPhoneNumber so the Pay button stays disabled until all fields are filled.
export const submitContactFormValidation = submitContactBase
  .refine(
    (data) => {
      return data.customerName.length > 0;
    },
    {
      message: 'Customer name is required',
      path: ['customerName'],
    },
  )
  .refine(
    (data) => {
      return (
        data.customerPhoneNumber !== null &&
        data.customerPhoneNumber !== undefined &&
        data.customerPhoneNumber !== ''
      );
    },
    {
      message: 'Customer phone number is required',
      path: ['customerPhoneNumber'],
    },
  );

export const goToStepSchema = z.object({
  cartId: z.uuid(),
  tenantId: z.uuid(),
  step: z.enum(ShopSteps),
});

export const goBackAStepSchema = z.object({
  cartId: z.uuid(),
  tenantId: z.uuid(),
});

export const completeOnlineBookingSchema = z.object({
  cartId: z.uuid(),
  tenantId: z.uuid(),
  // Optional for manual payment methods (e.g., check/cash)
  paymentMethodId: z.uuid().optional(),
  isWebsite: z.boolean(),
  ipAddress: z.string().optional(),
  // When true, enable Auto-Pay for the created customer using the chosen payment method
  configureAutoPay: z.boolean().optional().default(false),
});

const getChargesSchema = z.object({
  cartId: z.uuid(),
  tenantId: z.uuid(),
  preview: z
    .object({
      persistCouponDiscount: z.boolean().optional().default(true),
      daysOver: z.number().min(0).optional(),
      upsellItems: z
        .array(
          z.object({
            upsellItemId: z.uuid(),
            quantity: z.number().int().min(1),
          }),
        )
        .optional()
        .default([]),
    })
    .optional(),
});

export default {
  submitAddressAndWasteGenerator,
  submitPricing,
  submitNotesAndOthers,
  submitServiceAgreement,
  submitContact,
  submitServiceDate,
  completeOnlineBookingSchema,
  goBackAStepSchema,
  getChargesSchema,
};
